import {
  CloudRain,
  TrainFront,
  Zap,
  ArrowRight,
  RotateCcw,
  Link2,
  Play,
  Pause,
} from "lucide-react";
import { SCENARIOS } from "../data/city";
import { useCity } from "../state/CityContext";
import type { Parameters, ScenarioId } from "../simulation/types";
const controls: {
  key: keyof Parameters;
  label: string;
  min: number;
  max: number;
  unit: string;
  icon: string;
}[] = [
  {
    key: "rain",
    label: "Rainfall intensity",
    min: 0,
    max: 60,
    unit: "mm/h",
    icon: "water",
  },
  {
    key: "wind",
    label: "Wind speed",
    min: 0,
    max: 100,
    unit: "km/h",
    icon: "weather",
  },
  {
    key: "disruption",
    label: "Rail disruption",
    min: 0,
    max: 100,
    unit: "%",
    icon: "transport",
  },
  {
    key: "demand",
    label: "Energy demand",
    min: 80,
    max: 180,
    unit: "% baseline",
    icon: "power",
  },
];
export function SimulatePanel() {
  const { state, dispatch, current } = useCity();
  const scenario = SCENARIOS[state.run.scenario];
  return (
    <section
      className="mode-panel simulate-panel"
      aria-label="Simulation controls"
    >
      <span className="eyebrow panel-kicker">
        <span className="section-number">02</span> SIMULATE / SCENARIO LAB
      </span>
      <h1>
        Change the conditions.
        <br />
        <em>Watch the city react.</em>
      </h1>
      <p className="panel-intro">
        Choose a starting point, then shape the experiment.
      </p>
      <div className="scenario-picker">
        {(["storm", "rail", "energy"] as ScenarioId[]).map((id, i) => {
          const Icon = [CloudRain, TrainFront, Zap][i];
          return (
            <button
              key={id}
              aria-pressed={state.run.scenario === id}
              className={state.run.scenario === id ? "active" : ""}
              onClick={() => dispatch({ type: "scenario", id })}
            >
              <Icon size={19} />
              <span>{["Weather", "Transport", "Energy"][i]}</span>
              <span className="scenario-dot" />
            </button>
          );
        })}
      </div>
      <div className="scenario-description">
        <span className="mini-label">{scenario.label}</span>
        <h2>{scenario.name}</h2>
        <p>{scenario.description}</p>
      </div>
      <div className="parameter-controls">
        {controls.map((c) => (
          <div className={`parameter ${c.icon}`} key={c.key}>
            <label htmlFor={`param-${c.key}`}>{c.label}</label>
            <output htmlFor={`param-${c.key}`}>
              {state.run.parameters[c.key]} <small>{c.unit}</small>
            </output>
            <input
              id={`param-${c.key}`}
              type="range"
              min={c.min}
              max={c.max}
              step="1"
              value={state.run.parameters[c.key]}
              onChange={(e) =>
                dispatch({
                  type: "parameter",
                  key: c.key,
                  value: Number(e.target.value),
                })
              }
            />
            <div className="range-labels">
              <span>{c.min}</span>
              <span>{c.max}</span>
            </div>
          </div>
        ))}
      </div>
      <div className="causal-note">
        <Link2 size={15} />
        <div>
          <b>Everything is connected.</b>
          <p>
            Water stress raises energy pressure. Both increase mobility stress.
            Response crews reduce their assigned pressure over time.
          </p>
        </div>
      </div>
      <button
        className="primary-button"
        onClick={() => dispatch({ type: "play" })}
      >
        {state.playing ? <Pause size={16} /> : <Play size={16} />}{" "}
        {state.playing ? "Pause experiment" : "Run experiment"}
        <span className="button-aside">120 min</span>
      </button>
      <button
        className="secondary-button"
        onClick={() => dispatch({ type: "mode", mode: "respond" })}
      >
        Respond to the consequences <ArrowRight size={16} />
        <span className="count-bubble">{current.incidents.length}</span>
      </button>
      <button
        className="text-button"
        onClick={() => dispatch({ type: "scenario", id: "calm" })}
      >
        <RotateCcw size={13} /> Return to everyday conditions
      </button>
      <p className="small-note">
        Changing a parameter recalculates the whole experiment. Selecting a new
        scenario clears its responses.
      </p>
    </section>
  );
}
